import { shallowEqual, useSelector } from 'react-redux'
import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Card, CardContent, CircularProgress, Grid, Stack } from '@mui/material'
import i18n from 'simple-react-i18n'
import moment from 'moment'
import { flatten, groupBy, includes, orderBy, unzip } from 'lodash'
import { getSiteUrl } from '../../../../utils/mapUtils/SiteTypes'
import { STATION_NAME_ASSOCIATION, STATION_TYPE_CONSTANT } from '../../../../station/constants/StationConstants'
import Checkbox from '../../../../components/forms/Checkbox'
import NumberField from '../../../../components/forms/NumberField'
import Select from '../../../../components/forms/Select'
import RadioButtons from '../../../../components/forms/RadioButtons'
import { StyledFieldSet, StyledLegend } from '../../../../components/StyledElements'
import { CardTitle } from '../../../../components/card/NewCard'
import { IconButtonMUI } from '../../../../components/styled/Buttons'
import { getDate } from '../../../../utils/DateUtil'
import { getColorFromPalette } from '../../../../utils/ColorUtil'
import { execByType } from '../../../../utils/StationUtils'
import PiezometerStationAction from '../../../../station/actions/PiezometerStationAction'
import HydrometryAction from '../../../../hydrometry/actions/HydrometryAction'
import PluviometryAction from '../../../actions/PluviometryAction'
import Line from '../../../../components/echart/series/Line'
import Bar from '../../../../components/echart/series/Bar'
import { DAY, HOUR } from '../../../../quality/constants/ChartConstant'
import { MEASURE_COTE, PIEZO_TYPEID } from '../../../../piezometry/constants/PiezometryConstants'
import { PLUVIO_TYPEID } from '../../../constants/PluviometryConstant'
import {
    CUMUL_PERSO_AVERAGE,
    CUMUL_PERSO_MAX,
    CUMUL_PERSO_MIN,
    CUMUL_PERSO_SUM,
} from '../../../constants/PluvioOptionConstant'
import { LINKEDSTATIONS_TAB } from '../constants/PluvioChartTypes'

// Port direct de HydroSuiviAssociationsTab vers le suivi pluvio :
// - "Station principale" = pluviomètre courant (typeName 'pluviometry')
// - `promisePluviometerMeasuresStats` pour les stats pluvio principales
// - Le reste de la logique (fetch piezo/hydro/pluvio, construction séries, axes
//   secondaires) est identique au hydro. La sélection des associations n'est PAS
//   persistée (mémoire de session uniquement, contrairement au hydro qui la
//   stocke en localStorage).

const getPromise = (stationType) => execByType(stationType, {
    piezometry: () => PiezometerStationAction.promisePiezoChartMeasures,
    hydrometry: () => HydrometryAction.promiseHydroChronicMeasures,
    pluviometry: () => PluviometryAction.promisePluvioChronicMeasures,
})

const getGroups = () => [
    { code: 'MAX', name: 'Max' },
    { code: 'MIN', name: 'Min' },
    { code: 'AVERAGE', name: i18n.mean },
    { code: 'SUM', name: i18n.sum },
    { code: 'all', name: i18n.brute },
]

const PluvioSuiviAssociationsTab = ({
    tab,
    changeParent = () => {},
    minDate,
    maxDate,
    pluviometer,
    time,
    typeId: typeIdSelected,
}) => {
    const { associatedSites } = useSelector(store => ({
        associatedSites: store.StationReducer.associatedSites,
    }), shallowEqual)

    const [stats, setStats] = useState([])
    const [dataLoaded, setDataLoaded] = useState(false)

    const [statMin, setStatMin] = useState({})
    const [statMax, setStatMax] = useState({})
    const [pluvioMin, setPluvioMin] = useState(undefined)
    const [pluvioMax, setPluvioMax] = useState(undefined)
    const [statGroup, setStatGroup] = useState({})
    const [readyApply, setReadyApply] = useState(false)
    const changeStat = (func, obj, key, v) => {
        setReadyApply(true)
        return func({ ...obj, [key]: v })
    }
    const [allStatObj, setAllStatObj] = useState({ statMin, statMax, statGroup })

    const [stations, setStations] = useState([])
    const [baseSelectedAssociations, setBaseSelected] = useState([])
    const [selectedAssociations, setSelected] = useState(baseSelectedAssociations.filter(key => {
        const [, stationId] = key.split(':')
        const ids = [...associatedSites, { stationLinkedId: pluviometer.id }].map(({ stationLinkedId }) => stationLinkedId)
        return includes(ids, parseInt(stationId))
    }))
    const [preselectedAssociations, setPreselected] = useState(selectedAssociations)
    const changeSelected = (key, v) => {
        setPreselected(prev => v ? [...prev, key] : prev.filter(key2 => key2 !== key))
        setReadyApply(true)
    }
    const applyChanges = () => {
        setReadyApply(false)
        setAllStatObj({ statMin, statMax, statGroup })
        setSelected(preselectedAssociations)
        setBaseSelected([...baseSelectedAssociations.filter(baseKey => {
            const [, stationId] = baseKey.split(':')
            const preselectedKeysStationIds = preselectedAssociations.map(key => parseInt(key.split(':')[1]))
            const associatedSitesIds = associatedSites.map(({ stationLinkedId }) => stationLinkedId)
            return !includes(preselectedKeysStationIds, parseInt(stationId)) && !includes(associatedSitesIds, parseInt(stationId))
        }), ...preselectedAssociations])
    }

    const [axisPluvio, setAxisPluvio] = useState(true)
    const [nbTime, setNbTime] = useState(1)
    const [dayOrHour, setDayOrHour] = useState(DAY)
    const [cumul, setCumul] = useState(CUMUL_PERSO_SUM)
    const optionCumul = useMemo(() => [
        { value: CUMUL_PERSO_MAX, label: i18n.max },
        { value: CUMUL_PERSO_MIN, label: i18n.min },
        { value: CUMUL_PERSO_SUM, label: i18n.sum },
        { value: CUMUL_PERSO_AVERAGE, label: i18n.average },
        { value: 'all', label: i18n.brute },
    ], [])
    const optionDayHour = [
        { value: HOUR, label: i18n.hour },
        { value: DAY, label: i18n.day },
    ]

    useEffect(() => {
        const thisStation = {
            typeName: 'pluviometry',
            stationLinkedCode: pluviometer.code,
            stationLinkedName: pluviometer.name,
            stationLinkedId: pluviometer.id,
        }
        const piezos = associatedSites.filter(ass => ass.typeName === 'piezometry')
        const piezoPromises = piezos.map(ass => PiezometerStationAction.promisePiezoMeasuresStats(ass.stationLinkedId))
        const hydros = associatedSites.filter(ass => ass.typeName === 'hydrometry')
        const hydrosPromises = hydros.map(ass => HydrometryAction.promiseHydroStatistics(ass.stationLinkedId))
        const pluvios = [thisStation, ...associatedSites.filter(ass => ass.typeName === 'pluviometry')]
        const pluviosPromises = pluvios.map(ass => PluviometryAction.promisePluviometerMeasuresStats(ass.stationLinkedId))
        Promise.all([...piezoPromises, ...hydrosPromises, ...pluviosPromises]).then(jsonTab => {
            setStats(jsonTab)
            setStations([...piezos, ...hydros, ...pluvios])
            setDataLoaded(true)
        })
    }, [])

    useEffect(() => {
        if (!dataLoaded) return
        const grouped = groupBy(selectedAssociations, key => key.split(':')[0])
        const [promises, infos] = unzip(Object.keys(grouped).flatMap(stationType => {
            return grouped[stationType].map(key => {
                const [, stationId, typeId, codepoint, label] = key.split(':')
                const groupFunc = allStatObj.statGroup[key]
                    ?? (parseInt(typeId) === -2 || (stationType === 'pluviometry' && parseInt(typeId) === 1) ? 'SUM' : 'MAX')
                const input = {
                    stationId: parseInt(stationId),
                    displayCote: parseInt(typeId) === PIEZO_TYPEID.CHRONIC ? MEASURE_COTE.NGF : null,
                    dataType: parseInt(typeId),
                    codepoint: parseInt(codepoint),
                    startDate: minDate,
                    endDate: maxDate,
                    chartMode: true,
                    groupFunc,
                    group: groupFunc,
                    label,
                    link: stations.find(s => s.typeName === stationType && s.stationLinkedId === parseInt(stationId)),
                    stationType,
                    key,
                }
                if (stationType === 'pluviometry' && input.dataType === 1) {
                    const pluvioInput = {
                        ...input,
                        groupFunc: `${cumul}_${nbTime * (dayOrHour === DAY ? 24 : 1)}`,
                        group: `${cumul}_${nbTime * (dayOrHour === DAY ? 24 : 1)}`,
                    }
                    return [getPromise(stationType)(pluvioInput), pluvioInput]
                }
                return [getPromise(stationType)(input), input]
            })
        }))
        Promise.all(promises || []).then(jsonTab => {
            const [associationsSeries, associationsAxis] = jsonTab.reduce(([series, axis], measures, idx) => {
                const { label, link, stationType, dataType, codepoint, key } = infos[idx]
                const isPluvio = stationType === 'pluviometry' && dataType === PLUVIO_TYPEID.RAIN
                const axisName = label
                const isFirstAxis = dataType === typeIdSelected && stationType === 'pluviometry' && parseInt(infos[idx].stationId) === pluviometer.id
                const statFound = flatten(stats).find(s => s.typeId === dataType && s.label === label && s.codepoint === codepoint)
                const hasOtherPoint = stats.some(s => statFound?.typeId === s?.typeId && statFound?.codepoint !== s?.codepoint)
                const newAxis = axis.some(a => a.name === label) || isFirstAxis ? [] : [{
                    name: axisName,
                    isPluvio: axisPluvio ? isPluvio : false,
                    min: (isPluvio ? (pluvioMin ?? 0) : undefined) ?? allStatObj.statMin?.[key],
                    max: (isPluvio ? pluvioMax : undefined) ?? allStatObj?.statMax?.[key],
                    unit: statFound?.unit ?? '',
                }]
                if (!measures?.length) {
                    return [series, [...axis, ...newAxis]]
                }
                if (isPluvio) {
                    const pluvioSerie = Bar({
                        data: measures.map(m => ({ value: [m[0], m[1], statFound?.unit, m], unit: statFound?.unit })),
                        name: `${label} [${link.stationLinkedCode}] ${link.stationLinkedName}${hasOtherPoint ? ` - ${statFound.namePoint}` : ''}`,
                        isPluvio,
                        itemStyle: { normal: { color: statFound?.color || getColorFromPalette(idx) } },
                        axisName,
                        isFirstAxis,
                    })
                    return [[...series, pluvioSerie], [...axis, ...newAxis]]
                }
                const newSerie = (dataType === PIEZO_TYPEID.SAMPLE ? Bar : Line)({
                    data: measures.map(m => ({ value: m, symbolSize: 0, unit: statFound?.unit })),
                    name: `${label} [${link.stationLinkedCode}] ${link.stationLinkedName}${hasOtherPoint ? ` - ${statFound.namePoint}` : ''}`,
                    connectNulls: false,
                    showSymbol: true,
                    lineStyle: {
                        normal: {
                            color: statFound?.color || getColorFromPalette(idx),
                            width: statFound?.lineWidth || 2,
                            type: statFound?.lineType || 'solid',
                            opacity: statFound?.lineOpacity || 1,
                        },
                    },
                    itemStyle: { normal: { color: statFound?.color || getColorFromPalette(idx) } },
                    axisName,
                    isFirstAxis,
                })
                return [[...series, newSerie], [...axis, ...newAxis]]
            }, [[], []])
            changeParent({ associationsSeries, associationsAxis, selectedAssociations })
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAssociations, allStatObj, time, dataLoaded])

    if (tab !== LINKEDSTATIONS_TAB) return null

    if (!dataLoaded) {
        return <Grid container justifyContent='center' alignItems='center'><CircularProgress /></Grid>
    }

    const hasPluvioStation = stations.some(s => s.typeName === 'pluviometry')

    return (
        <Stack spacing={1}>
            {hasPluvioStation && (
                <Card>
                    <CardTitle title={`${i18n.associatedStations} ${i18n.and} ${i18n.additionalData}`} />
                    <CardContent>
                        <StyledFieldSet>
                            <StyledLegend>{i18n.rainDataSettings}</StyledLegend>
                            <Grid container justifyContent='center' alignItems='center' spacing={2}>
                                <Grid size={1}>
                                    <img src={getSiteUrl(STATION_NAME_ASSOCIATION.pluviometer)} style={{ maxHeight: '30px' }} alt='' />
                                </Grid>
                                <Grid size={2}><h6>{i18n.pluviometry}</h6></Grid>
                                <Grid size={3}>
                                    <Select
                                        options={optionCumul}
                                        label={i18n.modeCumul}
                                        value={cumul}
                                        onChange={setCumul}
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <NumberField
                                        title={dayOrHour === DAY ? i18n.numberDays : i18n.numberHours}
                                        value={nbTime}
                                        onChange={setNbTime}
                                        min={0}
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <RadioButtons
                                        colOption={6}
                                        label={i18n.modeDaysHours}
                                        elements={optionDayHour}
                                        selected={dayOrHour}
                                        onChange={setDayOrHour}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container justifyContent='center' alignItems='center' spacing={2}>
                                <Grid size={4}>
                                    <div className='padding-left-10'>{i18n.chartScale}</div>
                                </Grid>
                                <Grid size={2}>
                                    <NumberField value={pluvioMin} title='Min' onChange={setPluvioMin} />
                                </Grid>
                                <Grid size={2}>
                                    <NumberField value={pluvioMax} title='Max' onChange={setPluvioMax} />
                                </Grid>
                                <Grid size={4}>
                                    <Checkbox
                                        checked={axisPluvio}
                                        label='Histogramme en haut'
                                        onChange={() => setAxisPluvio(!axisPluvio)}
                                    />
                                </Grid>
                            </Grid>
                        </StyledFieldSet>
                    </CardContent>
                </Card>
            )}
            {stations.map((station, idx) => {
                const statsPanel = orderBy(stats[idx] ?? [], 'order').map(originalStat => {
                    const sStat = originalStat.typeId === -1 && station.typeName === 'pluviometry'
                        ? { ...originalStat, countTotal: i18n.calculatedMeasures }
                        : originalStat
                    const key = `${station.typeName}:${station.stationLinkedId}:${sStat.typeId}:${sStat.codepoint}:${sStat.label}`
                    const hasOtherPoint = stats[idx]?.some(s => originalStat.typeId === s.typeId && originalStat.codepoint !== s.codepoint)
                    const disabledFlag = (originalStat.typeId === typeIdSelected && station.stationLinkedId === pluviometer.id)
                        || (station.typeName === STATION_TYPE_CONSTANT.PLUVIOMETRY && originalStat.typeId === PLUVIO_TYPEID.RAIN && station.stationLinkedId === pluviometer.id)
                        ? { disabled: true } : {}
                    return (
                        <StyledFieldSet key={key}>
                            <StyledLegend>{`${sStat.label} ${hasOtherPoint ? `(${sStat.namePoint})` : ''}`}</StyledLegend>
                            <Grid container justifyContent='center' alignItems='center' spacing={1}>
                                <Grid size={1}>
                                    <Checkbox
                                        checked={preselectedAssociations.includes(key)}
                                        onChange={v => changeSelected(key, v)}
                                        disabled={originalStat.typeId === typeIdSelected && station.stationLinkedId === pluviometer.id}
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <Grid container justifyContent='center' alignItems='center' spacing={0}>
                                        <h6 style={{ margin: 1, fontSize: '1rem' }}>{`${sStat.countTotal} ${originalStat.typeId === -1 && station.typeName === 'pluviometry' ? '' : i18n.measures}`}</h6>
                                        <h6 style={{ margin: 1, fontSize: '1rem' }}>{`${i18n.fromDate} ${getDate(sStat.startDate)}`}</h6>
                                        <h6 style={{ margin: 1, fontSize: '1rem' }}>{`${i18n.to} ${getDate(sStat.endDate)}`}</h6>
                                    </Grid>
                                </Grid>
                                <Grid size={2}>
                                    <NumberField
                                        value={statMin[key]}
                                        title='Min'
                                        onChange={v => changeStat(setStatMin, statMin, key, v)}
                                        {...disabledFlag}
                                    />
                                </Grid>
                                <Grid size={2}>
                                    <NumberField
                                        value={statMax[key]}
                                        title='Max'
                                        onChange={v => changeStat(setStatMax, statMax, key, v)}
                                        {...disabledFlag}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <Select
                                        options={getGroups()}
                                        label={i18n.dailyRegroup}
                                        value={statGroup[key] ?? (parseInt(sStat.typeId) === PIEZO_TYPEID.SAMPLE || (station.typeName === 'pluviometry' && parseInt(sStat.typeId) === PLUVIO_TYPEID.RAIN) ? 'SUM' : 'MAX')}
                                        onChange={v => changeStat(setStatGroup, statGroup, key, v)}
                                        {...disabledFlag}
                                    />
                                </Grid>
                            </Grid>
                        </StyledFieldSet>
                    )
                })
                return (
                    <Grid spacing={3} key={`${station.stationLinkedCode}-${station.stationLinkedName}-${idx}`}>
                        <Card>
                            <CardContent>
                                <Grid container justifyContent='center' alignItems='center' spacing={3}>
                                    <Grid size={1}>
                                        <img src={getSiteUrl(STATION_NAME_ASSOCIATION[station.typeName])} style={{ maxHeight: '30px' }} alt='' />
                                    </Grid>
                                    <Grid size={2}><h6>{i18n[station.typeName]}</h6></Grid>
                                    <Grid size={8}>
                                        <h6>{`${station.stationLinkedCode} - ${station.stationLinkedName}`}</h6>
                                    </Grid>
                                </Grid>
                                <div>{statsPanel}</div>
                            </CardContent>
                        </Card>
                    </Grid>
                )
            })}
            <Grid container justifyContent='center' sx={{ bottom: 0, position: 'sticky', zIndex: 200 }}>
                <IconButtonMUI
                    tooltip={i18n.apply}
                    onClick={applyChanges}
                    icon='border_color'
                    className={`${readyApply ? 'pulse' : ''}`}
                />
            </Grid>
        </Stack>
    )
}

PluvioSuiviAssociationsTab.propTypes = {
    tab: PropTypes.string,
    pluviometer: PropTypes.shape({
        id: PropTypes.number,
        code: PropTypes.string,
        name: PropTypes.string,
    }),
    changeParent: PropTypes.func,
    minDate: PropTypes.number,
    maxDate: PropTypes.number,
    typeId: PropTypes.number,
    time: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
}

export default PluvioSuiviAssociationsTab
